import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Typography, Button, Card, CardContent, Avatar, TextField, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, Select, MenuItem, FormControl, InputLabel, Chip, Box, Skeleton, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Radio, Stack, Switch, FormControlLabel } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import Menu from '../components/Menu';
import CheckIcon from '@mui/icons-material/Check';
import Logs from '../components/dashboard/Logs';
import { Devices } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import api from '../utils/apiClient';
import secureStorage from '../utils/secureStorage';
import { useAuth } from '../hooks/useAuth';
import { getErrorMessage } from '../utils/errorMessage';
import { useNotifications } from '../context/NotificationContext';
import NotificationContainer from '../components/NotificationContainer';
import Commands from '../components/commands';
import { API_BASE as API_BASE_URL } from '../utils/apiConfig';
import { normalizeRole, getRoleLabel, isAdminOrSuperAdmin, isHosterOrAbove, ASSIGNABLE_ROLES } from '../utils/roles';
import { useMonitoring } from '../context/MonitoringContext';

const Profile = () => {
  const theme = useTheme();
  const { user_id } = useParams();
  const navigate = useNavigate();
  const { showError, showSuccess, showInfo } = useNotifications();
  const { t, i18n } = useTranslation('profile');
  
  // Базовый URL для действий (из Users.js используется REACT_APP_API_BASE_URL)
  const APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [bio, setBio] = useState('');
  
  // Новые состояния для полей из user_info
  const [telegram, setTelegram] = useState('');
  const [info, setInfo] = useState('');
  const [tags, setTags] = useState([]);
  const [dashboardLayoutDesktop, setDashboardLayoutDesktop] = useState({});
  const [dashboardLayoutMobile, setDashboardLayoutMobile] = useState({});
  // Данные предпросмотра дашборда
  const [dashboardPreview, setDashboardPreview] = useState(null);
  const [dashboardPreviewLoading, setDashboardPreviewLoading] = useState(false);
  
  // Новые состояния для полей из user_access
  const [hostings, setHostings] = useState('');
  const [hostingName, setHostingName] = useState('');
  
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [userData, setUserData] = useState(null);
  const { updateUser } = useAuth();
  const isOwnProfile = !user_id || user_id === 'me' || user_id === 'default' || (userData?.id && user_id === userData.id);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [userRole, setUserRole] = useState('client');
  const [viewedUserRole, setViewedUserRole] = useState('client');
  const [userLogs, setUserLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsTotal, setLogsTotal] = useState(0);

  // Реквизиты хостинга
  const [requisites, setRequisites] = useState(null);
  const [requisitesLoading, setRequisitesLoading] = useState(false);
  const [requisitesEditing, setRequisitesEditing] = useState(false);
  const [reqType, setReqType] = useState('legal');
  const [reqInn, setReqInn] = useState('');
  const [reqPhone, setReqPhone] = useState('');
  const [reqEmail, setReqEmail] = useState('');
  const [reqBankAccount, setReqBankAccount] = useState('');
  const [reqCorrAccount, setReqCorrAccount] = useState('');
  const [reqBik, setReqBik] = useState('');
  const [reqBankName, setReqBankName] = useState('');
  const [reqFullName, setReqFullName] = useState('');
  const [reqPassportSeries, setReqPassportSeries] = useState('');
  const [reqPassportNumber, setReqPassportNumber] = useState('');
  const [reqCompanyName, setReqCompanyName] = useState('');
  const [reqKpp, setReqKpp] = useState('');
  const [reqLegalAddress, setReqLegalAddress] = useState('');
  const [reqUsdtWallet, setReqUsdtWallet] = useState('');
  // Подписант справок и счетов хостинга и ставка НДС (пусто — без НДС)
  const [reqSignerTitle, setReqSignerTitle] = useState('');
  const [reqSignerName, setReqSignerName] = useState('');
  const [reqVatRate, setReqVatRate] = useState('');
  const [reqSignatureImage, setReqSignatureImage] = useState('');
  const [reqStampImage, setReqStampImage] = useState('');

  // Реквизиты клиента: массив (после миграции 019 у юзера их может быть несколько)
  const [clientReqList, setClientReqList] = useState([]);
  const [clientReqLoading, setClientReqLoading] = useState(false);
  const [crDialogOpen, setCrDialogOpen] = useState(false);
  const [crDialogMode, setCrDialogMode] = useState('add');
  const [crEditId, setCrEditId] = useState(null);
  const emptyCrForm = {
    type: 'individual', label: '', is_default: false,
    inn: '', kpp: '', company_name: '', legal_address: '',
    full_name: '', passport_series: '', passport_number: '',
    phone: '', email: '',
    bank_account: '', correspondent_account: '', bik: '', bank_name: '',
    usdt_wallet: ''
  };
  const [crForm, setCrForm] = useState(emptyCrForm);
  const [crDeleteOpen, setCrDeleteOpen] = useState(false);
  const [crDeleteId, setCrDeleteId] = useState(null);

  // Справка об оказанных услугах
  const [taxPeriodStart, setTaxPeriodStart] = useState('');
  const [taxPeriodEnd, setTaxPeriodEnd] = useState('');
  const [taxLoading, setTaxLoading] = useState(false);
  // Исполнители, у которых за период есть оплаченные счета клиента: по кнопке справки на каждого
  const [taxSuppliers, setTaxSuppliers] = useState(null);
  const [taxSuppliersLoading, setTaxSuppliersLoading] = useState(false);
  // Оплата в USDT у клиента — включает хостер своего клиента или администратор
  const [usdtEnabled, setUsdtEnabled] = useState(false);
  const [usdtSaving, setUsdtSaving] = useState(false);

  const { disabled: monitoringDisabled, setDisabled: setMonitoringDisabled, refresh: refreshMonitoring } = useMonitoring();
  const [mdDialogOpen, setMdDialogOpen] = useState(false);
  const [mdDialogTarget, setMdDialogTarget] = useState(false);
  const [mdSaving, setMdSaving] = useState(false);

  const handleUsdtSwitch = async (event) => {
    const next = event.target.checked;
    if (!user_id || usdtSaving) return;
    try {
      setUsdtSaving(true);
      const res = await api.put(`${APP_API_BASE_URL}users/${user_id}/usdt-payment`, { enabled: next });
      setUsdtEnabled(res?.data?.usdt_payment_enabled === true);
      showSuccess(t(next ? 'usdtEnabledOn' : 'usdtEnabledOff'));
    } catch (e) {
      showError(getErrorMessage(e, t('usdtToggleError')));
    } finally {
      setUsdtSaving(false);
    }
  };

  const handleMonitoringSwitchChange = (event) => {
    // mdDialogTarget == целевое значение monitoring_disabled (true = выключить мониторинг)
    setMdDialogTarget(!event.target.checked);
    setMdDialogOpen(true);
  };

  const handleMonitoringConfirm = async () => {
    if (mdSaving) return;
    try {
      setMdSaving(true);
      const resp = await api.patch(`${API_BASE_URL}me/monitoring-disabled`, { disabled: mdDialogTarget });
      const newVal = resp?.data?.monitoring_disabled === true;
      setMonitoringDisabled(newVal);
      try { await refreshMonitoring(); } catch (e) {}
      setMdDialogOpen(false);
      showSuccess(t(newVal ? 'monitoringDisabledSuccessOff' : 'monitoringDisabledSuccessOn'));
      if (newVal) {
        navigate('/bill');
      }
    } catch (e) {
      showError(getErrorMessage(e) || t('monitoringDisabledError'));
    } finally {
      setMdSaving(false);
    }
  };

  const handleMonitoringCancel = () => {
    if (mdSaving) return;
    setMdDialogOpen(false);
  };

  // Функция для загрузки данных профиля из API
  const loadProfileData = async () => {
    // Предотвращаем повторные вызовы
    if (profileLoading) {
      return;
    }

    // Предотвращаем повторную загрузку, если данные уже загружены
    if (profileLoaded) {
      return;
    }

    try {
      setProfileLoading(true);

      // Определяем, какой endpoint вызывать
      let endpoint = 'profile';
      if (user_id && user_id !== 'me') {
        endpoint = `profile/${user_id}`;
      }

      const response = await api.get(`${API_BASE_URL}${endpoint}`);

      if (response.data.success) {
        const profileData = response.data.data;

        // Обновляем состояния данными из API
        setName(profileData.name || t('noName'));
        setEmail(profileData.email || '');
        setTelegram(profileData.telegram || '');
        setInfo(profileData.info || '');
        setHostings(profileData.hostings || '');
        setHostingName(profileData.hosting_name || '');
        setBio(profileData.info_private || '');
        setUsdtEnabled(profileData.usdt_payment_enabled === true);

        // Устанавливаем роль просматриваемого пользователя
        setViewedUserRole(normalizeRole(profileData.role) || 'client');
        
        // Обрабатываем теги
        if (profileData.tags) {
          try {
            const parsedTags = typeof profileData.tags === 'string' ? JSON.parse(profileData.tags) : profileData.tags;
            if (Array.isArray(parsedTags)) {
              setTags(parsedTags);
            }
          } catch (error) {
            setTags([]);
          }
        }
        
        // Обрабатываем layout
        if (profileData.dashboard_layout_desktop) {
          try {
            const parsedLayout = typeof profileData.dashboard_layout_desktop === 'string' 
              ? JSON.parse(profileData.dashboard_layout_desktop) 
              : profileData.dashboard_layout_desktop;
            setDashboardLayoutDesktop(parsedLayout);
          } catch (error) {
            // Ошибка парсинга desktop layout
          }
        }
        
        if (profileData.dashboard_layout_mobile) {
          try {
            const parsedLayout = typeof profileData.dashboard_layout_mobile === 'string' 
              ? JSON.parse(profileData.dashboard_layout_mobile) 
              : profileData.dashboard_layout_mobile;
            setDashboardLayoutMobile(parsedLayout);
          } catch (error) {
            // Ошибка парсинга mobile layout
          }
        }
        
        // Загружаем предпросмотр дашборда для пользователя
        try {
          setDashboardPreviewLoading(true);
          const targetUserIdForPreview = (user_id && user_id !== 'me')
            ? user_id
            : (profileData.id_user || profileData.id || (userData && userData.id));
          if (targetUserIdForPreview) {
            const previewResp = await api.get(`${API_BASE_URL}dashboard_preview`, {
              params: { user_id: targetUserIdForPreview }
            });
            setDashboardPreview(previewResp.data);
          }
        } catch (e) {
          // Не удалось загрузить dashboard_preview
        } finally {
          setDashboardPreviewLoading(false);
        }
        
        // Роль уже получена из API, не нужно обновлять userData
        setProfileLoaded(true); // Отмечаем, что данные загружены
        showSuccess(t('profileLoadSuccess'));
        
      } else {
        showError(t('profileLoadErrorApi') + (response.data.message || t('profileLoadErrorUnknown')));
      }
    } catch (error) {
      if (error.response) {
        showError(`${t('common:error')} ${error.response.status}: ${getErrorMessage(error, t('profileLoadErrorUnknown'))}`);
      } else {
        showError(t('profileLoadErrorNetwork'));
      }
    } finally {
      setProfileLoading(false);
    }
  };

  // Логируем user_id при загрузке компонента
  useEffect(() => {
    // Сбрасываем флаг загрузки при изменении user_id
    setProfileLoaded(false);
    const role = secureStorage.getUserData()?.role;
    if (isHosterOrAbove(role)) {
      loadRequisites();
    }
    loadClientRequisites();
  }, [user_id, userData?.id]);

  // Загрузка логов пользователя и устройств
  const fetchLogs = async (opts = {}) => {
    const limit = Number.isInteger(opts.limit) ? opts.limit : 5; // стартуем с 5
    const offset = Number.isInteger(opts.offset) ? opts.offset : 0;
    const params = { limit, offset };
    // Передаем идентификатор просматриваемого пользователя
    if (user_id && user_id !== 'me') {
      params.user_id = user_id;
    } else if (userData?.id) {
      params.user_id = userData.id;
    }

    setLogsLoading(true);
    try {
      const userResp = await api.get(`${APP_API_BASE_URL}logs/user`, { params });

      const d = userResp?.data;
      const logs = Array.isArray(d) ? d : (Array.isArray(d?.logs) ? d.logs : (Array.isArray(d?.data) ? d.data : []));
      setUserLogs(logs);
      const totalCount = (typeof d?.count === 'number') ? d.count : (typeof d?.total === 'number' ? d.total : logs.length);
      setLogsTotal(totalCount);
    } catch (e) {
      // Ошибка загрузки логов
    } finally {
      setLogsLoading(false);
    }
  };

  // Загрузка ещё логов: увеличиваем offset и подгружаем +20
  const handleLoadMoreLogs = async () => {
    const currentCount = Array.isArray(userLogs) ? userLogs.length : 0;
    try {
      setLogsLoading(true);
      const params = { limit: 20, offset: currentCount };
      if (user_id && user_id !== 'me') params.user_id = user_id; else if (userData?.id) params.user_id = userData.id;
      const resp = await api.get(`${APP_API_BASE_URL}logs/user`, { params });
      const d = resp?.data;
      const next = Array.isArray(d) ? d : (Array.isArray(d?.logs) ? d.logs : (Array.isArray(d?.data) ? d.data : []));
      setUserLogs((prev) => Array.isArray(prev) ? [...prev, ...next] : next);
      const totalCount = (typeof d?.count === 'number') ? d.count : (typeof d?.total === 'number' ? d.total : (currentCount + next.length));
      setLogsTotal(totalCount);
    } catch (e) {
      // Ошибка дозагрузки логов
    } finally {
      setLogsLoading(false);
    }
  };

  // Загружаем данные пользователя из secureStorage при монтировании компонента
  useEffect(() => {
    const loadUserData = () => {
      const parsedUserData = secureStorage.getUserData();
      if (parsedUserData && parsedUserData.email) {
        setUserData(parsedUserData);
        setUserRole(normalizeRole(parsedUserData.role) || 'client');
      }
    };

    // Загружаем данные при монтировании
    loadUserData();

    // Слушаем изменения в localStorage (например, из другой вкладки)
    const handleStorageChange = (event) => {
      if (event.key === '_ud' || event.key === 'userData') {
        loadUserData();
      }
    };

    // Загружаем данные профиля из API после загрузки данных пользователя
    setTimeout(() => {
      loadProfileData();
      fetchLogs();
      const role = secureStorage.getUserData()?.role;
      if (isHosterOrAbove(role)) {
        loadRequisites();
      }
    }, 100);

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Скелетоны для оборудования (пока нет реальных данных)
  const deviceSkeletons = Array(3).fill(null);

  const handleSave = async () => {
    const targetUserId = (user_id && user_id !== 'me') ? user_id : (userData?.id || null);
    if (!targetUserId) {
      showError(t('userIdNotFound'));
      return;
    }

    const editPayload = {
      name: name || '',
      telegram: telegram || '',
      info: info || '',
      info_private: bio || '',
      tags: Array.isArray(tags) ? tags : []
    };

    showInfo(t('profileSaving'));

    // 1) Обновление информации профиля
    try {
      const res = await api.post(`${APP_API_BASE_URL}profile/edit/${targetUserId}`, editPayload);
      const msg = (typeof res?.data === 'string' && res.data) || res?.data?.message || t('profileInfoUpdated');
      showSuccess(msg);
      // Обновляем кэш/шапку, если правим свой профиль
      if (isOwnProfile) {
        updateUser({ name: editPayload.name, telegram: editPayload.telegram, info: editPayload.info });
      }
    } catch (err) {
      showError(getErrorMessage(err, t('profileInfoUpdateError')));
      // Не выходим, чтобы попытаться сохранить роль, если доступно
    }

    // 2) Обновление роли (только если админ открыл чужой профиль; своему менять роль нельзя)
    if (userData && isAdminOrSuperAdmin(userData.role) && !isOwnProfile) {
      try {
        const resRole = await api.post(`${APP_API_BASE_URL}access-control/role/${targetUserId}`, { role: viewedUserRole });
        const msgRole = (typeof resRole?.data === 'string' && resRole.data) || resRole?.data?.message || t('profileRoleUpdated');
        showSuccess(msgRole);
      } catch (err) {
        showError(getErrorMessage(err, t('profileRoleUpdateError')));
      }
    }

    setIsEditing(false);
  };

  // Загрузка реквизитов хостинга
  const loadRequisites = async () => {
    if (requisitesLoading) return;
    try {
      setRequisitesLoading(true);
      const res = await api.get(`${APP_API_BASE_URL}hosting/requisites`);
      const data = res?.data?.requisites;
      if (data) {
        setRequisites(data);
        setReqType(data.type || 'legal');
        setReqInn(data.inn || '');
        setReqPhone(data.phone || '');
        setReqEmail(data.email || '');
        setReqBankAccount(data.bank_account || '');
        setReqCorrAccount(data.correspondent_account || '');
        setReqBik(data.bik || '');
        setReqBankName(data.bank_name || '');
        setReqFullName(data.full_name || '');
        setReqPassportSeries(data.passport_series || '');
        setReqPassportNumber(data.passport_number || '');
        setReqCompanyName(data.company_name || '');
        setReqKpp(data.kpp || '');
        setReqLegalAddress(data.legal_address || '');
        setReqUsdtWallet(data.usdt_wallet || '');
        setReqSignerTitle(data.signer_title || '');
        setReqSignerName(data.signer_name || '');
        setReqVatRate(data.vat_rate === null || data.vat_rate === undefined ? '' : String(Number(data.vat_rate)));
        setReqSignatureImage(data.signature_image || '');
        setReqStampImage(data.stamp_image || '');
      }
    } catch (e) {
      // Реквизитов нет - это нормально
    } finally {
      setRequisitesLoading(false);
    }
  };

  // Сохранение реквизитов
  const handleSaveRequisites = async () => {
    try {
      const payload = {
        hosting_id: hostings ? hostings : undefined,
        type: reqType,
        inn: reqInn,
        phone: reqPhone,
        email: reqEmail,
        bank_account: reqBankAccount,
        correspondent_account: reqCorrAccount,
        bik: reqBik,
        bank_name: reqBankName,
        full_name: (reqType === 'individual' || reqType === 'ip') ? reqFullName : '',
        passport_series: reqType === 'individual' ? reqPassportSeries : '',
        passport_number: reqType === 'individual' ? reqPassportNumber : '',
        company_name: reqType === 'legal' ? reqCompanyName : '',
        kpp: reqType === 'legal' ? reqKpp : '',
        legal_address: (reqType === 'legal' || reqType === 'ip') ? reqLegalAddress : '',
        usdt_wallet: reqUsdtWallet,
        signer_title: reqSignerTitle,
        signer_name: reqSignerName,
        vat_rate: reqVatRate === '' ? null : Number(String(reqVatRate).replace(',', '.')),
        signature_image: reqSignatureImage || null,
        stamp_image: reqStampImage || null
      };

      if (requisites) {
        await api.put(`${APP_API_BASE_URL}hosting/requisites`, payload);
      } else {
        await api.post(`${APP_API_BASE_URL}hosting/requisites`, payload);
      }
      showSuccess(t('requisitesSaved'));
      setRequisitesEditing(false);
      await loadRequisites();
    } catch (err) {
      showError(getErrorMessage(err, t('requisitesSaveError')));
    }
  };

  const handleHostingImageUpload = (kind) => (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      showError(t('reqImageInvalidFormat'));
      return;
    }
    if (file.size > 500 * 1024) {
      showError(t('reqImageTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const b64 = typeof result === 'string' ? (result.split(',')[1] || '') : '';
      if (kind === 'signature') setReqSignatureImage(b64);
      if (kind === 'stamp') setReqStampImage(b64);
    };
    reader.onerror = () => showError(t('reqImageInvalidFormat'));
    reader.readAsDataURL(file);
  };

  // Загрузка реквизитов клиента (массив)
  const loadClientRequisites = async () => {
    if (clientReqLoading) return;
    const effectiveUserId = isOwnProfile
      ? (userData?.id || null)
      : (user_id && user_id !== 'me' && user_id !== 'default' ? user_id : null);
    if (!effectiveUserId) return;
    try {
      setClientReqLoading(true);
      const res = await api.get(`${APP_API_BASE_URL}client-requisites/${effectiveUserId}`);
      const list = Array.isArray(res?.data?.requisites) ? res.data.requisites : [];
      setClientReqList(list);
    } catch (e) {
      setClientReqList([]);
    } finally {
      setClientReqLoading(false);
    }
  };

  const openAddClientReq = () => {
    setCrDialogMode('add');
    setCrEditId(null);
    // Первая запись будет дефолтной автоматически на бэке, но в форме показываем флаг честно.
    setCrForm({ ...emptyCrForm, is_default: clientReqList.length === 0 });
    setCrDialogOpen(true);
  };

  const openEditClientReq = (item) => {
    setCrDialogMode('edit');
    setCrEditId(item.id);
    setCrForm({
      type: item.type || 'individual',
      label: item.label || '',
      is_default: !!item.is_default,
      inn: item.inn || '',
      kpp: item.kpp || '',
      company_name: item.company_name || '',
      legal_address: item.legal_address || '',
      full_name: item.full_name || '',
      passport_series: item.passport_series || '',
      passport_number: item.passport_number || '',
      phone: item.phone || '',
      email: item.email || '',
      bank_account: item.bank_account || '',
      correspondent_account: item.correspondent_account || '',
      bik: item.bik || '',
      bank_name: item.bank_name || '',
      usdt_wallet: item.usdt_wallet || ''
    });
    setCrDialogOpen(true);
  };

  const closeClientReqDialog = () => {
    setCrDialogOpen(false);
    setCrEditId(null);
  };

  const updateCrField = (field, value) => setCrForm((prev) => ({ ...prev, [field]: value }));

  const handleSaveClientReqDialog = async () => {
    if (!isOwnProfile) {
      closeClientReqDialog();
      return;
    }
    const effectiveUserId = userData?.id || null;
    if (!effectiveUserId) {
      showError(t('userIdNotFound'));
      return;
    }
    const f = crForm;
    const payload = {
      user_id: effectiveUserId,
      type: f.type,
      label: f.label || null,
      is_default: !!f.is_default,
      inn: f.inn,
      phone: f.phone,
      email: f.email,
      bank_account: f.bank_account,
      correspondent_account: f.correspondent_account,
      bik: f.bik,
      bank_name: f.bank_name,
      full_name: (f.type === 'individual' || f.type === 'ip') ? f.full_name : '',
      passport_series: f.type === 'individual' ? f.passport_series : '',
      passport_number: f.type === 'individual' ? f.passport_number : '',
      company_name: f.type === 'legal' ? f.company_name : '',
      kpp: f.type === 'legal' ? f.kpp : '',
      legal_address: (f.type === 'legal' || f.type === 'ip') ? f.legal_address : '',
      usdt_wallet: f.usdt_wallet
    };
    try {
      if (crDialogMode === 'edit' && crEditId) {
        await api.put(`${APP_API_BASE_URL}client-requisites/by-id/${crEditId}`, payload);
      } else {
        await api.post(`${APP_API_BASE_URL}client-requisites`, payload);
      }
      showSuccess(t('requisitesSaved'));
      closeClientReqDialog();
      await loadClientRequisites();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === 'CLIENT_REQUISITES_DEFAULT_REQUIRED') {
        showError(t('clientReqDefaultRequired'));
      } else {
        showError(getErrorMessage(err, t('requisitesSaveError')));
      }
    }
  };

  const handleSetDefaultClientReq = async (id) => {
    try {
      await api.patch(`${APP_API_BASE_URL}client-requisites/by-id/${id}/default`);
      showSuccess(t('clientReqDefaultSuccess'));
      await loadClientRequisites();
    } catch (err) {
      showError(getErrorMessage(err, t('requisitesSaveError')));
    }
  };

  const askDeleteClientReq = (id) => {
    setCrDeleteId(id);
    setCrDeleteOpen(true);
  };

  const confirmDeleteClientReq = async () => {
    if (!crDeleteId) return;
    try {
      await api.delete(`${APP_API_BASE_URL}client-requisites/by-id/${crDeleteId}`);
      showSuccess(t('clientReqDeleteSuccess'));
      setCrDeleteOpen(false);
      setCrDeleteId(null);
      await loadClientRequisites();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === 'CLIENT_REQUISITES_LAST') {
        showError(t('clientReqDeleteLast'));
      } else {
        showError(getErrorMessage(err, t('requisitesSaveError')));
      }
    }
  };

  // Справка об оказанных услугах: отдельно от каждого исполнителя (WAYMORR — подписка, хостинг — размещение)
  const taxUserId = isOwnProfile
    ? (userData?.id || null)
    : (user_id && user_id !== 'me' && user_id !== 'default' ? user_id : null);
  const taxPeriodValid = Boolean(taxPeriodStart && taxPeriodEnd && taxPeriodStart <= taxPeriodEnd);

  useEffect(() => {
    if (!taxPeriodValid || !taxUserId) { setTaxSuppliers(null); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setTaxSuppliersLoading(true);
        const res = await api.get(`${APP_API_BASE_URL}tax_report`, {
          params: { mode: 'suppliers', period_start: taxPeriodStart, period_end: taxPeriodEnd, user_id: taxUserId },
        });
        if (!cancelled) setTaxSuppliers(Array.isArray(res.data?.suppliers) ? res.data.suppliers : []);
      } catch (e) {
        if (!cancelled) { setTaxSuppliers(null); showError(getErrorMessage(e, t('tax:error'))); }
      } finally {
        if (!cancelled) setTaxSuppliersLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPeriodStart, taxPeriodEnd, taxUserId]);

  const handleDownloadTaxReport = async (supplierKey) => {
    if (!taxPeriodValid) {
      showError(t('tax:periodRequired'));
      return;
    }
    if (!taxUserId) {
      showError(t('userIdNotFound'));
      return;
    }
    try {
      setTaxLoading(supplierKey);
      const res = await api.get(`${APP_API_BASE_URL}tax_report`, {
        params: { period_start: taxPeriodStart, period_end: taxPeriodEnd, user_id: taxUserId, supplier: supplierKey },
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `spravka_${supplierKey === 'waymorr' ? 'waymorr' : 'hosting'}_${taxPeriodStart}_${taxPeriodEnd}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Profile: ошибка скачивания справки', e);
      // Ответ об ошибке приходит как blob — достаём текст сервера (нет счетов, нет реквизитов хостинга)
      let message = t('tax:error');
      try {
        const text = await e?.response?.data?.text?.();
        const body = text ? JSON.parse(text) : null;
        if (body?.error) message = body.error;
      } catch (_) { /* остаётся общий текст */ }
      showError(message);
    } finally {
      setTaxLoading(false);
    }
  };

  const handleAddTag = (tag) => {
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // Функция для получения роли пользователя
  const getUserRole = () => {
    const parsed = secureStorage.getUserData();
    return parsed?.role || 'user';
  };

  // Функция для стилизации ролей (как в Users.js)
  const getRoleStyle = (role) => {
    switch (normalizeRole(role)) {
      case 'super_admin':
        return {
          backgroundColor: '#9c27b0', // Фиолетовый
          color: '#fff',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.8em',
        };
      case 'admin':
        return {
          backgroundColor: '#f44336', // Красный
          color: '#fff',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.8em',
        };
      case 'hoster':
        return {
          backgroundColor: '#ffeb3b', // Желтый
          color: '#000',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.8em',
        };
      default:
        return {
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.primary.contrastText,
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.8em',
        };
    }
  };

  // Конвертация значения хешрейта в зависимости от алгоритма
  const convertHashrate = (value, unit, algorithm) => {
    if (value == null || isNaN(value)) return { value: 0, unit: 'TH/s' };
    const normalize = (u) => (typeof u === 'string' ? u.trim().toUpperCase() : '');
    let u = normalize(unit);
    if (u === 'GG/S') u = 'GH/S';
    
    // Определяем целевую единицу в зависимости от алгоритма
    let targetUnit = 'TH/s';
    if (algorithm === 'Scrypt') {
      targetUnit = 'GH/s';
    } else if (algorithm === 'Equihash') {
      targetUnit = 'kSol/s';
    }
    
    const factors = {
      'H/S': 1 / 1e12,
      'KH/S': 1 / 1e9,
      'MH/S': 1 / 1e6,
      'GH/S': 1 / 1e3,
      'TH/S': 1,
      'PH/S': 1e3,
      'EH/S': 1e6,
    };
    
    // Конвертируем в TH/s сначала
    const factor = factors[u] ?? 1;
    const thValue = Number(value) * factor;
    
    // Затем конвертируем в целевую единицу
    if (targetUnit === 'GH/s') {
      return { value: thValue * 1000, unit: 'GH/s' };
    } else if (targetUnit === 'kSol/s') {
      return { value: thValue, unit: 'kSol/s' };
    } else {
      return { value: thValue, unit: 'TH/s' };
    }
  };

  // Обратная совместимость - старая функция для TH/s
  const convertHashrateToTH = (value, unit) => {
    const result = convertHashrate(value, unit, 'SHA-256');
    return result.value;
  };

  // Нормализация статуса (поддержка как строк, так и boolean для обратной совместимости)
  const normalizeStatusValue = (status) => {
    if (status == null) return null;
    if (status === true) return 'online';
    if (status === false) return 'offline';
    if (typeof status === 'string') {
      const normalized = status.trim().toLowerCase();
      return normalized || null;
    }
    return null;
  };

  // Берем последний статус аптайма из структуры items
  const getLatestUptimeStatusFromData = (deviceData) => {
    const uptime = deviceData?.uptime;
    if (!uptime || uptime.length === 0) return null;
    const sorted = [...uptime].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    // Нормализуем статус из uptime
    return normalizeStatusValue(sorted[0]?.status ?? null);
  };

  // Функция для получения started_at из открытого диапазона offline
  const getOfflineStartedAt = (uptimeData) => {
    if (!uptimeData || !Array.isArray(uptimeData) || uptimeData.length === 0) {
      return null;
    }

    // Ищем запись со статусом offline и ended_at === null
    const offlineRecord = uptimeData.find(
      record => {
        const normalizedStatus = normalizeStatusValue(record.status);
        return normalizedStatus === 'offline' && record.ended_at === null;
      }
    );

    return offlineRecord?.started_at || null;
  };

  // Функция для расчета времени нахождения в offline
  const getOfflineDuration = (startedAt) => {
    if (!startedAt) return null;

    const now = new Date();
    const startDate = new Date(startedAt);
    const diffMs = now - startDate;

    // Если разница отрицательная или некорректная, возвращаем null
    if (diffMs < 0 || isNaN(diffMs)) return null;

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}д ${diffHours % 24}ч`;
    } else if (diffHours > 0) {
      return `${diffHours}ч ${diffMinutes % 60}м`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes}м`;
    } else {
      return `${diffSeconds}с`;
    }
  };

  // Выбор worker из пулов по приоритетам
  const getWorkerFromPools = (pools) => {
    if (!pools || !Array.isArray(pools) || pools.length === 0) {
      return 'N/A';
    }
    const activePool = pools.find(pool => pool.active === true);
    if (activePool?.worker) return activePool.worker;
    const nullPriorityPool = pools.find(pool => pool.priority === null);
    if (nullPriorityPool?.worker) return nullPriorityPool.worker;
    const poolsWithPriority = pools.filter(pool => pool.priority !== null && pool.priority !== undefined);
    if (poolsWithPriority.length > 0) {
      const minPriorityPool = poolsWithPriority.reduce((min, pool) => (pool.priority < min.priority ? pool : min));
      if (minPriorityPool?.worker) return minPriorityPool.worker;
    }
    const anyWorkerPool = pools.find((pool) => typeof pool.worker === 'string' && pool.worker.trim() !== '');
    if (anyWorkerPool) return anyWorkerPool.worker;
    return 'N/A';
  };

  // Функция для получения правил алертов устройства
  const getDeviceAlertRules = (device) => {
    return device?.alert_rule_devices && device.alert_rule_devices.length > 0
      ? device.alert_rule_devices[0]
      : null;
  };

  // Функция для расчета цвета температуры/вентилятора
  const getMetricColor = (value, min, max) => {
    if (min == null || max == null) {
      return theme.palette.text.secondary; // Серый, если нет правил
    }

    // Если выше max - красный
    if (value > max) {
      return theme.palette.error.main;
    }

    // Плавный переход от зеленого к оранжевому
    const ratio = (value - min) / (max - min);
    const clampedRatio = Math.max(0, Math.min(1, ratio));

    // От зеленого (120°) к оранжевому (30°)
    const hue = 120 - clampedRatio * 90;
    return `hsl(${hue}, 70%, 50%)`;
  };

  // Функция для расчета цвета хешрейта
  const getHashrateColor = (value, min, max) => {
    if (min == null || max == null) {
      return theme.palette.success.main; // Зеленый по умолчанию, если нет правил
    }

    // Если выше max или ниже min - красный
    if (value > max || value < min) {
      return theme.palette.error.main;
    }

    // Вычисляем расстояние от границ
    const midpoint = (min + max) / 2;
    const range = max - min;

    if (range === 0) {
      return theme.palette.success.main;
    }

    // Расстояние от ближайшей границы
    const distanceFromMin = Math.abs(value - min);
    const distanceFromMax = Math.abs(value - max);
    const minDistance = Math.min(distanceFromMin, distanceFromMax);

    // Нормализуем расстояние (0 = на границе, 1 = в центре)
    const normalizedDistance = (minDistance / (range / 2));
    const clampedDistance = Math.max(0, Math.min(1, normalizedDistance));

    // Цвет от желтого (у границ) к зеленому (в центре)
    // Зеленый: hue = 120, Желтый: hue = 60
    const hue = 60 + clampedDistance * 60;
    return `hsl(${hue}, 70%, 50%)`;
  };

  // Преобразование устройства и метрик к строкам таблицы (минимально под Dashboard)
  const createDeviceData = (device, items = []) => {
    const matchingData = Array.isArray(items) ? items.find((data) => data.device_id === device.id) : null;
    const uptimeStatus = getLatestUptimeStatusFromData(matchingData);

    let hashrateDisplay = 'N/A';
    if (uptimeStatus === 'online') {
      const lastMetric = matchingData?.metrics?.[matchingData.metrics.length - 1];
      if (lastMetric && lastMetric.hashrate != null) {
        const rawValue = Number(lastMetric.hashrate || 0);
        const unit = device.hashrate_unit || device.hashrateUnit || 'TH/s';
        const result = convertHashrate(rawValue, unit, device.algorithm);
        hashrateDisplay = `${result.value.toFixed(2)} ${result.unit}`;
      }
    }

    return {
      id: device.id,
      model: device.model,
      modelName: device.model_name || null,
      status: uptimeStatus,
      algorithm: device.algorithm,
      mac: device.mac,
      hashrate: hashrateDisplay,
      temperature: (() => {
        const lastMetric = matchingData?.metrics?.[matchingData.metrics.length - 1];
        if (!lastMetric) return [];
        return [
          lastMetric.temperature0,
          lastMetric.temperature1,
          lastMetric.temperature2,
          lastMetric.temperature3,
          lastMetric.temperature4,
          lastMetric.temperature5,
        ].filter((v) => v !== null && v !== undefined);
      })(),
      fan: (() => {
        const lastMetric = matchingData?.metrics?.[matchingData.metrics.length - 1];
        if (!lastMetric) return [];
        return [
          lastMetric.fan_speed0,
          lastMetric.fan_speed1,
          lastMetric.fan_speed2,
          lastMetric.fan_speed3,
        ].filter((v) => v !== null && v !== undefined);
      })(),
      worker: getWorkerFromPools(device.pools),
      device: device, // Сохраняем ссылку на полный объект устройства для доступа к alert_rule_devices
      latestMetrics: matchingData?.metrics?.[matchingData.metrics.length - 1] || null,
      uptimeData: matchingData?.uptime || [], // Сохраняем данные uptime для расчета длительности offline
    };
  };

  // Стили статуса (как в Dashboard)
  const getStatusStyle = (status) => {
    // Нормализуем статус для сравнения
    const normalizedStatus = typeof status === 'string' ? status.toLowerCase() : status;

    if (normalizedStatus === 'online') {
      return {
        backgroundColor: '#4caf50', // Зеленый для Online
        color: '#fff',
        borderRadius: '12px',
        padding: '4px 8px',
        textAlign: 'center',
      };
    } else if (normalizedStatus === 'offline') {
      return {
        backgroundColor: '#f44336', // Красный для Offline
        color: '#fff',
        borderRadius: '12px',
        padding: '4px 8px',
        textAlign: 'center',
      };
    } else if (normalizedStatus === null || normalizedStatus === undefined) {
      return {
        backgroundColor: '#9e9e9e', // Серый для null/undefined
        color: '#fff',
        borderRadius: '12px',
        padding: '4px 8px',
        textAlign: 'center',
      };
    } else {
      return {
        backgroundColor: '#2196f3', // Синий для других статусов (repair, maintenance и т.д.)
        color: '#fff',
        borderRadius: '12px',
        padding: '4px 8px',
        textAlign: 'center',
      };
    }
  };

  // Проверяем, должна ли показываться кнопка "Просмотр"
  const shouldShowPreviewButton = () => {
    return isHosterOrAbove(userRole);
  };

  const handleSelectAll = (event) => {
    setSelectAll(event.target.checked);
    const rowsCount = deviceRows && deviceRows.length > 0 ? deviceRows.length : deviceSkeletons.length;
    if (event.target.checked) {
      const allRowIndices = Array.from({ length: rowsCount }, (_, i) => i);
      setSelectedRows(allRowIndices);
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (index) => {
    const newSelectedRows = selectedRows.includes(index)
      ? selectedRows.filter((rowIndex) => rowIndex !== index)
      : [...selectedRows, index];
    setSelectedRows(newSelectedRows);
  };

  const handleDeviceClick = (deviceId) => {
    if (deviceId) {
      navigate(`/device/${deviceId}`);
    }
  };

  // Данные устройств из предпросмотра дашборда
  // Фильтруем устройства: не показываем устройства со статусом "delete" или "removed"
  const dataDashboard = Array.isArray(dashboardPreview?.items) ? dashboardPreview.items : [];
  const deviceRows = Array.isArray(dashboardPreview?.devices)
    ? dashboardPreview.devices
        .filter((device) => {
          const status = device.status?.toLowerCase();
          return status !== 'delete' && status !== 'removed';
        })
        .map((device) => createDeviceData(device, dataDashboard))
    : [];

  return (
    <div style={{ backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Menu style={{ position: 'relative', zIndex: 10 }} />
      <Container maxWidth={false} style={{ padding: '20px', marginTop: '20px', maxWidth: '1200px', textAlign: 'right' }}>
        <Button variant="outlined" onClick={() => setIsEditing(true)} style={{ marginBottom: '10px', marginRight: '10px' }}>
          {t('editButton')}
        </Button>
        {/* Показываем кнопку "Просмотр" только для admin и manager */}
        {shouldShowPreviewButton() && (
          <Button variant="outlined" style={{ marginBottom: '10px' }}>
            {t('previewButton')}
          </Button>
        )}
        <Card>
          <CardContent style={{ textAlign: 'left', backgroundColor: theme.palette.background.paper }}>
            <Avatar
              alt="User Avatar"
              src="https://via.placeholder.com/150"
              sx={{ width: 100, height: 100, borderRadius: '5px', margin: '0' }}
            />
            {isEditing ? (
              <>
                <TextField
                  label={t('labelName')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  label={t('labelEmail')}
                  value={email}
                  fullWidth
                  margin="normal"
                  InputProps={{ readOnly: true }}
                  helperText={t('labelEmailChangeDisabled')}
                />
                {/* Показываем поле роли только для админа/супер-админа и только на чужом профиле */}
                {userData && isAdminOrSuperAdmin(userData.role) && !isOwnProfile && (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>{t('labelRole')}</InputLabel>
                    <Select
                      value={viewedUserRole}
                      label={t('labelRole')}
                      onChange={(e) => {
                        const newRole = e.target.value;
                        setViewedUserRole(newRole);
                      }}
                    >
                      {ASSIGNABLE_ROLES.map(r => (
                        <MenuItem key={r} value={r}>{getRoleLabel(r, t)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <TextField
                  label={t('labelTelegram')}
                  value={telegram}
                  onChange={(e) => setTelegram(e.target.value)}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  label={t('labelAdditionalInfo')}
                  value={info}
                  onChange={(e) => setInfo(e.target.value)}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={3}
                />
                {/* Показываем поле хостингов только если роль не user */}
                {userData && (viewedUserRole !== 'client' || isHosterOrAbove(userRole)) && (
                  <TextField
                    label={t('labelHosting')}
                    value={hostings}
                    onChange={(e) => setHostings(e.target.value)}
                    fullWidth
                    margin="normal"
                  />
                )}
                {/* Показываем поля для тегов и BIO только если роль не user */}
                {userData && (viewedUserRole !== 'client' || isHosterOrAbove(userRole)) && (
                  <>
                    <TextField
                      label={t('labelAddTag')}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddTag(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      fullWidth
                      margin="normal"
                      placeholder={t('labelAddTagPlaceholder')}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          onDelete={() => handleRemoveTag(tag)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                    <TextField
                      label={t('labelInternalInfo')}
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      fullWidth
                      margin="normal"
                      multiline
                      rows={4}
                    />
                  </>
                )}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton color="primary" onClick={handleSave} size="large">
                    <CheckIcon />
                  </IconButton>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="h5" component="div" style={{ marginTop: '10px', color: theme.palette.text.primary }}>
                  {t('labelName')}: {profileLoading ? <Skeleton variant="text" width={150} /> : name}
                  <span style={{ marginLeft: '10px' }}>
                    <span style={getRoleStyle(viewedUserRole)}>
                      {getRoleLabel(viewedUserRole, t)}
                    </span>
                  </span>
                </Typography>
                <Typography variant="body2" style={{ color: theme.palette.text.secondary, marginTop: '8px' }}>
                  {t('labelEmail')}: {profileLoading ? <Skeleton variant="text" width={200} /> : email}
                </Typography>
                <Typography variant="body2" style={{ color: theme.palette.text.secondary, marginTop: '8px' }}>
                  {t('labelTelegram')}: {profileLoading ? <Skeleton variant="text" width={180} /> : (telegram || t('notSpecified'))}
                </Typography>
                <Typography variant="body2" style={{ color: theme.palette.text.secondary, marginTop: '8px' }}>
                  {t('labelAdditionalInfo')}: {profileLoading ? <Skeleton variant="text" width={250} /> : (info || t('notSpecifiedFemale'))}
                </Typography>
                {/* Показываем хостинги только если роль не user */}
                {userData && (viewedUserRole !== 'client' || isHosterOrAbove(userRole)) && (
                  <Typography variant="body2" style={{ color: theme.palette.text.secondary, marginTop: '8px' }}>
                    {t('labelHostings')}: {profileLoading ? <Skeleton variant="text" width={160} /> : (hostingName || hostings || t('notSpecifiedPlural'))}
                  </Typography>
                )}
                {/* Внутренняя информация: блок с тегами и описанием */}
                {userData && (viewedUserRole !== 'client' || isHosterOrAbove(userRole)) && (
                  <>
                    <Divider style={{ marginTop: '12px', marginBottom: '12px' }} />
                    <Typography variant="subtitle1" style={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                      {t('internalInfoTitle')}
                    </Typography>
                    <Box sx={{ mt: '8px' }}>
                      <Typography variant="body2" style={{ color: theme.palette.text.secondary, marginBottom: '5px' }}>
                        {t('labelTags')}
                      </Typography>
                      {profileLoading ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: '12px' }} />
                          <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: '12px' }} />
                          <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: '12px' }} />
                        </Box>
                      ) : (
                        tags.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {tags.map((tag, index) => (
                              <Chip
                                key={index}
                                label={tag}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" style={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                            {t('noTags')}
                          </Typography>
                        )
                      )}
                    </Box>
                    <Typography variant="body2" style={{ marginTop: '8px', color: theme.palette.text.secondary }}>
                      {t('internalInfoTitle')}: {profileLoading ? <Skeleton variant="text" width={300} /> : (bio || t('notSpecifiedFemale'))}
                    </Typography>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
        {/* Реквизиты хостинга - только на своём профиле, иначе админ видел бы свой хостинг в чужих профилях */}
        {isHosterOrAbove(userRole) && isOwnProfile && (
          <Card style={{ marginTop: '20px' }}>
            <CardContent style={{ textAlign: 'left', backgroundColor: theme.palette.background.paper }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" style={{ color: theme.palette.text.primary }}>
                  {t('requisitesTitle')}
                </Typography>
                {!requisitesEditing && isOwnProfile && (
                  <Button variant="outlined" size="small" onClick={() => setRequisitesEditing(true)}>
                    {requisites ? t('requisitesEditButton') : t('requisitesFillButton')}
                  </Button>
                )}
              </Box>
              {requisitesEditing ? (
                <>
                  <FormControl fullWidth margin="normal" size="small">
                    <InputLabel>{t('reqTypeLabel')}</InputLabel>
                    <Select value={reqType} label={t('reqTypeLabel')} onChange={(e) => setReqType(e.target.value)}>
                      <MenuItem value="legal">{t('reqTypeLegalOnly')}</MenuItem>
                      <MenuItem value="ip">{t('reqTypeIp')}</MenuItem>
                      <MenuItem value="individual">{t('reqTypeIndividual')}</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField label={t('reqInn')} value={reqInn} onChange={(e) => setReqInn(e.target.value)} fullWidth margin="normal" size="small" />
                  {reqType === 'legal' && (
                    <>
                      <TextField label={t('reqCompanyName')} value={reqCompanyName} onChange={(e) => setReqCompanyName(e.target.value)} fullWidth margin="normal" size="small" />
                      <TextField label={t('reqKpp')} value={reqKpp} onChange={(e) => setReqKpp(e.target.value)} fullWidth margin="normal" size="small" />
                      <TextField label={t('reqLegalAddress')} value={reqLegalAddress} onChange={(e) => setReqLegalAddress(e.target.value)} fullWidth margin="normal" size="small" />
                    </>
                  )}
                  {reqType === 'ip' && (
                    <>
                      <TextField label={t('reqFullName')} value={reqFullName} onChange={(e) => setReqFullName(e.target.value)} fullWidth margin="normal" size="small" />
                      <TextField label={t('reqLegalAddress')} value={reqLegalAddress} onChange={(e) => setReqLegalAddress(e.target.value)} fullWidth margin="normal" size="small" />
                    </>
                  )}
                  {reqType === 'individual' && (
                    <>
                      <TextField label={t('reqFullName')} value={reqFullName} onChange={(e) => setReqFullName(e.target.value)} fullWidth margin="normal" size="small" />
                      <TextField label={t('reqPassportSeries')} value={reqPassportSeries} onChange={(e) => setReqPassportSeries(e.target.value)} fullWidth margin="normal" size="small" />
                      <TextField label={t('reqPassportNumber')} value={reqPassportNumber} onChange={(e) => setReqPassportNumber(e.target.value)} fullWidth margin="normal" size="small" />
                    </>
                  )}
                  <TextField label={t('reqPhone')} value={reqPhone} onChange={(e) => setReqPhone(e.target.value)} fullWidth margin="normal" size="small" />
                  <TextField label={t('labelEmail')} value={reqEmail} onChange={(e) => setReqEmail(e.target.value)} fullWidth margin="normal" size="small" />
                  <TextField label={t('reqBankAccount')} value={reqBankAccount} onChange={(e) => setReqBankAccount(e.target.value)} fullWidth margin="normal" size="small" />
                  <TextField label={t('reqCorrAccount')} value={reqCorrAccount} onChange={(e) => setReqCorrAccount(e.target.value)} fullWidth margin="normal" size="small" />
                  <TextField label={t('reqBik')} value={reqBik} onChange={(e) => setReqBik(e.target.value)} fullWidth margin="normal" size="small" />
                  <TextField label={t('reqBankName')} value={reqBankName} onChange={(e) => setReqBankName(e.target.value)} fullWidth margin="normal" size="small" />
                  <TextField label={t('reqUsdtWallet')} value={reqUsdtWallet} onChange={(e) => setReqUsdtWallet(e.target.value)} fullWidth margin="normal" size="small" helperText={t('reqUsdtWalletHint')} />
                  <TextField label={t('reqSignerTitle')} value={reqSignerTitle} onChange={(e) => setReqSignerTitle(e.target.value)} fullWidth margin="normal" size="small" placeholder={t('reqSignerTitlePlaceholder')} />
                  <TextField label={t('reqSignerName')} value={reqSignerName} onChange={(e) => setReqSignerName(e.target.value)} fullWidth margin="normal" size="small" placeholder={t('reqSignerNamePlaceholder')} helperText={t('reqSignerHint')} />
                  <TextField label={t('reqVatRate')} value={reqVatRate} onChange={(e) => setReqVatRate(e.target.value.replace(/[^0-9.,]/g, ''))} fullWidth margin="normal" size="small" inputProps={{ inputMode: 'decimal' }} helperText={t('reqVatRateHint')} />
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('reqImagesHeader')}</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {[
                        { kind: 'signature', label: t('reqSignatureLabel'), value: reqSignatureImage, setter: setReqSignatureImage },
                        { kind: 'stamp', label: t('reqStampLabel'), value: reqStampImage, setter: setReqStampImage }
                      ].map((item) => (
                        <Box key={item.kind} sx={{ flex: '1 1 220px', minWidth: 220 }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>{item.label}</Typography>
                          {item.value ? (
                            <Box sx={{ mb: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1, display: 'inline-block', maxWidth: '100%' }}>
                              <img src={`data:image/png;base64,${item.value}`} alt={t('reqImagePreviewAlt')} style={{ maxWidth: '100%', maxHeight: 120, display: 'block' }} />
                            </Box>
                          ) : null}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button component="label" variant="outlined" size="small">
                              {t('reqUploadButton')}
                              <input hidden type="file" accept="image/png,image/jpeg" onChange={handleHostingImageUpload(item.kind)} />
                            </Button>
                            {item.value && (
                              <Button variant="text" size="small" color="error" onClick={() => item.setter('')}>
                                {t('reqRemoveButton')}
                              </Button>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {t('reqImageHint')}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" onClick={() => setRequisitesEditing(false)}>{t('common:cancel')}</Button>
                    <Button variant="contained" onClick={handleSaveRequisites}>{t('common:save')}</Button>
                  </Box>
                </>
              ) : (
                <>
                  {requisitesLoading ? (
                    <Box>
                      <Skeleton variant="text" width={300} />
                      <Skeleton variant="text" width={250} />
                      <Skeleton variant="text" width={200} />
                    </Box>
                  ) : requisites ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', rowGap: 1, columnGap: 2 }}>
                      <Typography color="text.secondary" variant="body2">{t('reqTypeLabel')}</Typography>
                      <Typography variant="body2">{requisites.type === 'legal' ? t('reqTypeLegalDisplay') : requisites.type === 'ip' ? t('reqTypeIpDisplay') : t('reqTypeIndividualDisplay')}</Typography>
                      {requisites.type === 'legal' && requisites.company_name && (
                        <>
                          <Typography color="text.secondary" variant="body2">{t('reqCompanyLabel')}</Typography>
                          <Typography variant="body2">{requisites.company_name}</Typography>
                        </>
                      )}
                      {(requisites.type === 'individual' || requisites.type === 'ip') && requisites.full_name && (
                        <>
                          <Typography color="text.secondary" variant="body2">{t('reqFullName')}</Typography>
                          <Typography variant="body2">{requisites.full_name}</Typography>
                        </>
                      )}
                      {requisites.inn && (
                        <>
                          <Typography color="text.secondary" variant="body2">{t('reqInn')}</Typography>
                          <Typography variant="body2">{requisites.inn}</Typography>
                        </>
                      )}
                      {requisites.type === 'legal' && requisites.kpp && (
                        <>
                          <Typography color="text.secondary" variant="body2">{t('reqKpp')}</Typography>
                          <Typography variant="body2">{requisites.kpp}</Typography>
                        </>
                      )}
                      {(requisites.type === 'legal' || requisites.type === 'ip') && requisites.legal_address && (
                        <>
                          <Typography color="text.secondary" variant="body2">{t('reqLegalAddress')}</Typography>
                          <Typography variant="body2">{requisites.legal_address}</Typography>
                        </>
                      )}
                      {requisites.bank_name && (
                        <>
                          <Typography color="text.secondary" variant="body2">{t('reqBankLabel')}</Typography>
                          <Typography variant="body2">{requisites.bank_name}</Typography>
                        </>
                      )}
                      {requisites.bank_account && (
                        <>
                          <Typography color="text.secondary" variant="body2">{t('reqBankAccountShort')}</Typography>
                          <Typography variant="body2">{requisites.bank_account}</Typography>
                        </>
                      )}
                      {requisites.bik && (
                        <>
                          <Typography color="text.secondary" variant="body2">{t('reqBik')}</Typography>
                          <Typography variant="body2">{requisites.bik}</Typography>
                        </>
                      )}
                      {requisites.phone && (
                        <>
                          <Typography color="text.secondary" variant="body2">{t('reqPhone')}</Typography>
                          <Typography variant="body2">{requisites.phone}</Typography>
                        </>
                      )}
                      {requisites.email && (
                        <>
                          <Typography color="text.secondary" variant="body2">{t('labelEmail')}</Typography>
                          <Typography variant="body2">{requisites.email}</Typography>
                        </>
                      )}
                      {(requisites.signer_title || requisites.signer_name) && (
                        <>
                          <Typography color="text.secondary" variant="body2">{t('reqSigner')}</Typography>
                          <Typography variant="body2">{[requisites.signer_title, requisites.signer_name].filter(Boolean).join(' / ')}</Typography>
                        </>
                      )}
                      <Typography color="text.secondary" variant="body2">{t('reqVatRate')}</Typography>
                      <Typography variant="body2">{requisites.vat_rate === null || requisites.vat_rate === undefined ? t('reqVatNone') : `${Number(requisites.vat_rate)}%`}</Typography>
                      {requisites.usdt_wallet && (
                        <>
                          <Typography color="text.secondary" variant="body2">{t('reqUsdtWallet')}</Typography>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{requisites.usdt_wallet}</Typography>
                        </>
                      )}
                      {(requisites.signature_image || requisites.stamp_image) && (
                        <>
                          <Typography color="text.secondary" variant="body2">{t('reqImagesHeader')}</Typography>
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {requisites.signature_image && (
                              <Box sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{t('reqSignatureLabel')}</Typography>
                                <img src={`data:image/png;base64,${requisites.signature_image}`} alt={t('reqSignatureLabel')} style={{ maxWidth: 180, maxHeight: 100, display: 'block' }} />
                              </Box>
                            )}
                            {requisites.stamp_image && (
                              <Box sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{t('reqStampLabel')}</Typography>
                                <img src={`data:image/png;base64,${requisites.stamp_image}`} alt={t('reqStampLabel')} style={{ maxWidth: 180, maxHeight: 100, display: 'block' }} />
                              </Box>
                            )}
                          </Box>
                        </>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">{t('requisitesNotFilled')}</Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}
        {isOwnProfile && normalizeRole(viewedUserRole) === 'client' && (
          <Card style={{ marginTop: '20px' }}>
            <CardContent style={{ textAlign: 'left', backgroundColor: theme.palette.background.paper }}>
              <Typography variant="h6" style={{ color: theme.palette.text.primary, marginBottom: 8 }}>
                {t('monitoringSectionTitle')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    {monitoringDisabled ? t('monitoringDisabledHintOff') : t('monitoringDisabledHintOn')}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!monitoringDisabled}
                      onChange={handleMonitoringSwitchChange}
                      disabled={mdSaving}
                    />
                  }
                  label={monitoringDisabled ? t('monitoringSwitchOff') : t('monitoringSwitchOn')}
                  labelPlacement="start"
                  sx={{ ml: 0 }}
                />
              </Box>
            </CardContent>
          </Card>
        )}
        {/* Реквизиты клиента: список с возможностью добавления/редактирования/удаления и выбора дефолтных */}
        {normalizeRole(viewedUserRole) === 'client' && (
        <Card style={{ marginTop: '20px' }}>
          <CardContent style={{ textAlign: 'left', backgroundColor: theme.palette.background.paper }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" style={{ color: theme.palette.text.primary }}>
                {t('clientRequisitesTitle')}
              </Typography>
              {isOwnProfile && (
                <Button variant="outlined" size="small" onClick={openAddClientReq}>
                  {t('clientReqAddButton')}
                </Button>
              )}
            </Box>
            {clientReqLoading ? (
              <Box>
                <Skeleton variant="text" width={300} />
                <Skeleton variant="text" width={250} />
                <Skeleton variant="text" width={200} />
              </Box>
            ) : clientReqList.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t('clientReqEmpty')}</Typography>
            ) : (
              <Stack spacing={1.5}>
                {clientReqList.map((item) => {
                  const typeLabel = item.type === 'legal' ? t('reqTypeLegalDisplay')
                    : item.type === 'ip' ? t('reqTypeIpDisplay')
                    : t('reqTypeIndividualDisplay');
                  const titleParts = [];
                  if (item.label) titleParts.push(item.label);
                  if (item.type === 'legal' && item.company_name) titleParts.push(item.company_name);
                  if ((item.type === 'ip' || item.type === 'individual') && item.full_name) titleParts.push(item.full_name);
                  const subtitle = titleParts.length ? titleParts.join(' · ') : typeLabel;
                  return (
                    <Paper key={item.id} variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{subtitle}</Typography>
                          <Chip size="small" label={typeLabel} />
                          {item.is_default && <Chip size="small" color="primary" label={t('clientReqDefaultBadge')} />}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                          {item.inn && <Typography variant="caption" color="text.secondary">{t('reqInn')}: {item.inn}</Typography>}
                          {item.type === 'legal' && item.kpp && <Typography variant="caption" color="text.secondary">{t('reqKpp')}: {item.kpp}</Typography>}
                          {item.bank_name && <Typography variant="caption" color="text.secondary">{t('reqBankLabel')}: {item.bank_name}</Typography>}
                          {item.bank_account && <Typography variant="caption" color="text.secondary">{t('reqBankAccountShort')}: {item.bank_account}</Typography>}
                        </Box>
                      </Box>
                      {isOwnProfile && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                          {!item.is_default && (
                            <Button size="small" onClick={() => handleSetDefaultClientReq(item.id)}>{t('clientReqSetDefault')}</Button>
                          )}
                          <Button size="small" variant="outlined" onClick={() => openEditClientReq(item)}>{t('clientReqEditButton')}</Button>
                          <Button size="small" color="error" onClick={() => askDeleteClientReq(item.id)} disabled={clientReqList.length <= 1}>{t('clientReqDeleteButton')}</Button>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>
        )}

        <Dialog open={crDialogOpen} onClose={closeClientReqDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{crDialogMode === 'edit' ? t('clientReqDialogEditTitle') : t('clientReqDialogAddTitle')}</DialogTitle>
          <DialogContent dividers>
            <TextField
              label={t('clientReqLabel')}
              placeholder={t('clientReqLabelPlaceholder')}
              helperText={t('clientReqLabelHint')}
              value={crForm.label}
              onChange={(e) => updateCrField('label', e.target.value)}
              fullWidth margin="normal" size="small"
            />
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel>{t('reqTypeLabel')}</InputLabel>
              <Select value={crForm.type} label={t('reqTypeLabel')} onChange={(e) => updateCrField('type', e.target.value)}>
                <MenuItem value="individual">{t('reqTypeIndividual')}</MenuItem>
                <MenuItem value="ip">{t('reqTypeIp')}</MenuItem>
                <MenuItem value="legal">{t('reqTypeLegalOnly')}</MenuItem>
              </Select>
            </FormControl>
            <TextField label={t('reqInn')} value={crForm.inn} onChange={(e) => updateCrField('inn', e.target.value)} fullWidth margin="normal" size="small" />
            {crForm.type === 'legal' && (
              <>
                <TextField label={t('reqCompanyName')} value={crForm.company_name} onChange={(e) => updateCrField('company_name', e.target.value)} fullWidth margin="normal" size="small" />
                <TextField label={t('reqKpp')} value={crForm.kpp} onChange={(e) => updateCrField('kpp', e.target.value)} fullWidth margin="normal" size="small" />
                <TextField label={t('reqLegalAddress')} value={crForm.legal_address} onChange={(e) => updateCrField('legal_address', e.target.value)} fullWidth margin="normal" size="small" />
              </>
            )}
            {crForm.type === 'ip' && (
              <>
                <TextField label={t('reqFullName')} value={crForm.full_name} onChange={(e) => updateCrField('full_name', e.target.value)} fullWidth margin="normal" size="small" />
                <TextField label={t('reqLegalAddress')} value={crForm.legal_address} onChange={(e) => updateCrField('legal_address', e.target.value)} fullWidth margin="normal" size="small" />
              </>
            )}
            {crForm.type === 'individual' && (
              <>
                <TextField label={t('reqFullName')} value={crForm.full_name} onChange={(e) => updateCrField('full_name', e.target.value)} fullWidth margin="normal" size="small" />
                <TextField label={t('reqPassportSeries')} value={crForm.passport_series} onChange={(e) => updateCrField('passport_series', e.target.value)} fullWidth margin="normal" size="small" />
                <TextField label={t('reqPassportNumber')} value={crForm.passport_number} onChange={(e) => updateCrField('passport_number', e.target.value)} fullWidth margin="normal" size="small" />
              </>
            )}
            <TextField label={t('reqPhone')} value={crForm.phone} onChange={(e) => updateCrField('phone', e.target.value)} fullWidth margin="normal" size="small" />
            <TextField label={t('labelEmail')} value={crForm.email} onChange={(e) => updateCrField('email', e.target.value)} fullWidth margin="normal" size="small" />
            <TextField label={t('reqBankAccount')} value={crForm.bank_account} onChange={(e) => updateCrField('bank_account', e.target.value)} fullWidth margin="normal" size="small" />
            <TextField label={t('reqCorrAccount')} value={crForm.correspondent_account} onChange={(e) => updateCrField('correspondent_account', e.target.value)} fullWidth margin="normal" size="small" />
            <TextField label={t('reqBik')} value={crForm.bik} onChange={(e) => updateCrField('bik', e.target.value)} fullWidth margin="normal" size="small" />
            <TextField label={t('reqBankName')} value={crForm.bank_name} onChange={(e) => updateCrField('bank_name', e.target.value)} fullWidth margin="normal" size="small" />
            <TextField label={t('reqUsdtWallet')} value={crForm.usdt_wallet} onChange={(e) => updateCrField('usdt_wallet', e.target.value)} fullWidth margin="normal" size="small" />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Radio
                checked={!!crForm.is_default}
                onChange={(e) => updateCrField('is_default', e.target.checked)}
                disabled={crDialogMode === 'edit' && crForm.is_default}
                size="small"
              />
              <Typography variant="body2">{t('clientReqDefaultBadge')}</Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeClientReqDialog}>{t('common:cancel')}</Button>
            <Button variant="contained" onClick={handleSaveClientReqDialog}>{t('common:save')}</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={crDeleteOpen} onClose={() => setCrDeleteOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('clientReqDeleteConfirmTitle')}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2">{t('clientReqDeleteConfirmBody')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCrDeleteOpen(false)}>{t('common:cancel')}</Button>
            <Button variant="contained" color="error" onClick={confirmDeleteClientReq}>{t('clientReqConfirmDelete')}</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={mdDialogOpen} onClose={handleMonitoringCancel} maxWidth="xs" fullWidth>
          <DialogTitle>
            {mdDialogTarget ? t('monitoringConfirmTitleOff') : t('monitoringConfirmTitleOn')}
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2">
              {mdDialogTarget ? t('monitoringConfirmBodyOff') : t('monitoringConfirmBodyOn')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleMonitoringCancel} disabled={mdSaving}>{t('common:cancel')}</Button>
            <Button
              variant="contained"
              color={mdDialogTarget ? 'error' : 'primary'}
              onClick={handleMonitoringConfirm}
              disabled={mdSaving}
            >
              {mdDialogTarget ? t('monitoringConfirmActionOff') : t('monitoringConfirmActionOn')}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Оплата в USDT: видна и меняется только хостером клиента или администратором */}
        {!isOwnProfile && isHosterOrAbove(userRole) && (
          <Card style={{ marginTop: '20px' }}>
            <CardContent style={{ textAlign: 'left', backgroundColor: theme.palette.background.paper }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" style={{ color: theme.palette.text.primary }}>{t('usdtSectionTitle')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t(usdtEnabled ? 'usdtHintOn' : 'usdtHintOff')}</Typography>
                </Box>
                <FormControlLabel
                  control={<Switch checked={usdtEnabled} onChange={handleUsdtSwitch} disabled={usdtSaving} />}
                  label={t(usdtEnabled ? 'usdtSwitchOn' : 'usdtSwitchOff')}
                  labelPlacement="start"
                  sx={{ ml: 0 }}
                />
              </Box>
            </CardContent>
          </Card>
        )}
        {/* Справка об оказанных услугах */}
        <Card style={{ marginTop: '20px' }}>
          <CardContent style={{ textAlign: 'left', backgroundColor: theme.palette.background.paper }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" style={{ color: theme.palette.text.primary }}>
                {t('tax:title')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <TextField
                label={t('tax:periodStart')}
                type="date"
                value={taxPeriodStart}
                onChange={(e) => setTaxPeriodStart(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={t('tax:periodEnd')}
                type="date"
                value={taxPeriodEnd}
                onChange={(e) => setTaxPeriodEnd(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            {taxPeriodStart && taxPeriodEnd && taxPeriodStart > taxPeriodEnd && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>{t('tax:periodInvalid')}</Typography>
            )}
            {(userRole !== 'user' || isOwnProfile) && taxPeriodValid && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {taxSuppliersLoading && <Typography variant="body2" color="text.secondary">{t('tax:searching')}</Typography>}
                {!taxSuppliersLoading && taxSuppliers && taxSuppliers.length === 0 && (
                  <Typography variant="body2" color="text.secondary">{t('tax:noPaidBills')}</Typography>
                )}
                {!taxSuppliersLoading && (taxSuppliers || []).map((sp) => (
                  <Button
                    key={sp.key}
                    variant="contained"
                    onClick={() => handleDownloadTaxReport(sp.key)}
                    disabled={Boolean(taxLoading) || sp.requisites_missing}
                    title={sp.requisites_missing ? t('tax:hostingRequisitesMissing') : undefined}
                  >
                    {taxLoading === sp.key ? t('tax:generating') : t('tax:downloadFrom', { name: sp.name })}
                  </Button>
                ))}
              </Box>
            )}
            {clientReqList.length === 0 && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
                {t('tax:requisitesMissing')}.
                {isOwnProfile && (
                  <>
                    {' '}<Button size="small" onClick={openAddClientReq}>{t('tax:fillRequisites')}</Button>
                  </>
                )}
              </Typography>
            )}
          </CardContent>
        </Card>
        <div style={{ marginTop: '20px' }}>
          <Logs logsData={{ user: userLogs, loading: logsLoading, onLoadMore: handleLoadMoreLogs, canLoadMore: userLogs.length < logsTotal }} />
        </div>
        <div style={{ marginTop: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', fontSize: '20px', color: theme.palette.text.primary, marginBottom: '10px' }}>
            <Devices style={{ marginRight: '5px', color: theme.palette.text.primary }} />
            {t('equipment')}
          </div>
          <div style={{
            borderRadius: '5px',
            padding: '10px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            border: 'none',
            backgroundColor: theme.palette.background.paper,
          }}>
            <TableContainer component={Paper} style={{ marginTop: '20px', overflowX: 'auto', backgroundColor: theme.palette.background.paper }}>
              <Table sx={{ minWidth: 650 }} aria-label="device table">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>{t('deviceColumn')}</TableCell>
                    <TableCell>{t('modelColumn')}</TableCell>
                    <TableCell>{t('statusColumn')}</TableCell>
                    <TableCell>{t('algorithmColumn')}</TableCell>
                    <TableCell>{t('macColumn')}</TableCell>
                    <TableCell>{t('hashrateColumn')}</TableCell>
                    <TableCell>{t('temperatureColumn')}</TableCell>
                    <TableCell>{t('fanColumn')}</TableCell>
                    <TableCell>{t('workerColumn')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deviceRows.length > 0 ? (
                    deviceRows.map((row, index) => (
                      <TableRow key={row.id || index}>
                        <TableCell>
                          <Checkbox
                            checked={selectedRows.includes(index)}
                            onChange={() => handleRowSelect(index)}
                          />
                        </TableCell>
                        <TableCell>
                          {row.model ? (
                            <span
                              style={{ color: theme.palette.primary.main, cursor: 'pointer' }}
                              onClick={() => handleDeviceClick(row.id)}
                            >
                              {row.model}
                            </span>
                          ) : 'N/A'}
                        </TableCell>
                        <TableCell>{row.modelName || 'N/A'}</TableCell>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                          {(() => {
                            let statusText;

                            if (row.status === 'online') {
                              statusText = 'Online';
                            } else if (row.status === 'offline') {
                              const startedAt = getOfflineStartedAt(row.uptimeData);
                              const duration = getOfflineDuration(startedAt);
                              statusText = duration ? `Offline (${duration})` : 'Offline';
                            } else if (row.status === null) {
                              statusText = 'N/A';
                            } else {
                              statusText = row.status.charAt(0).toUpperCase() + row.status.slice(1);
                            }

                            return (
                              <span style={getStatusStyle(row.status)}>
                                {statusText}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell>{row.algorithm || 'N/A'}</TableCell>
                        <TableCell>{row.mac || 'N/A'}</TableCell>
                        <TableCell>
                          {(() => {
                            const alertRules = getDeviceAlertRules(row.device);
                            const latestMetrics = row.latestMetrics;

                            if (row.hashrate === '-' || row.hashrate === 'N/A' || !latestMetrics?.hashrate) {
                              return (
                                <span style={{
                                  color: theme.palette.text.secondary,
                                  fontWeight: 'normal'
                                }}>
                                  {row.hashrate}
                                </span>
                              );
                            }

                            const hashrateValue = latestMetrics.hashrate;
                            const hashrateMin = alertRules?.hashrate_min;
                            const hashrateMax = alertRules?.hashrate_max;
                            const color = getHashrateColor(hashrateValue, hashrateMin, hashrateMax);

                            return (
                              <span style={{
                                color: color,
                                fontWeight: 'bold'
                              }}>
                                {row.hashrate}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const alertRules = getDeviceAlertRules(row.device);
                            const latestMetrics = row.latestMetrics;

                            if (!row.temperature || row.temperature.length === 0) {
                              return 'N/A';
                            }

                            return (
                              <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {row.temperature.map((temp, index) => {
                                  const tempKey = `temperature${index}`;
                                  const actualTemp = latestMetrics?.[tempKey];
                                  const min = alertRules?.[`${tempKey}_min`];
                                  const max = alertRules?.[`${tempKey}_max`];
                                  const color = getMetricColor(actualTemp, min, max);

                                  return (
                                    <span key={index} style={{ color, fontWeight: 'bold' }}>
                                      {temp}°
                                    </span>
                                  );
                                })}
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const alertRules = getDeviceAlertRules(row.device);
                            const latestMetrics = row.latestMetrics;

                            if (!row.fan || row.fan.length === 0) {
                              return 'N/A';
                            }

                            return (
                              <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'nowrap' }}>
                                {row.fan.map((fanSpeed, index) => {
                                  const fanKey = `fan_speed${index}`;
                                  const actualFan = latestMetrics?.[fanKey];
                                  const min = alertRules?.[`${fanKey}_min`];
                                  const max = alertRules?.[`${fanKey}_max`];
                                  const color = getMetricColor(actualFan, min, max);

                                  return (
                                    <span key={index} style={{ color, fontWeight: 'bold' }}>
                                      {fanSpeed}
                                    </span>
                                  );
                                })}
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell>{row.worker || 'N/A'}</TableCell>
                      </TableRow>
                    ))
                  ) : dashboardPreviewLoading ? (
                    deviceSkeletons.map((_, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Checkbox
                            checked={selectedRows.includes(index)}
                            onChange={() => handleRowSelect(index)}
                          />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width={120} />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width={100} />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width={80} />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width={100} />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width={140} />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width={90} />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width={60} />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width={40} />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" width={80} />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} align="center" style={{ padding: '40px', color: theme.palette.text.secondary }}>
                        {t('noEquipment')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
          <div style={{ paddingTop: '10px' }}>
            <Commands
              selectedRows={selectedRows}
              deviceIds={selectedRows.map(index => deviceRows[index]?.id).filter(Boolean)}
            />
          </div>
        </div>
      </Container>
      <NotificationContainer />
    </div>
  );
};

export default Profile;
